import { getAuthConfig } from "../config/environment";

/**
 * Talks to Amazon Cognito straight from the browser. The app client is public (no secret),
 * so password sign-in uses Cognito's JSON API and Google sign-in uses the hosted UI with PKCE.
 */

export interface AuthTokens {
  accessToken: string;
  /** Absent when refreshing: Cognito keeps the existing refresh token valid. */
  refreshToken?: string;
  expiresIn: number;
}

/** A Cognito error, with its exception name (e.g. "NotAuthorizedException") in `code`. */
export class CognitoError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "CognitoError";
    this.code = code;
  }
}

function cognitoEndpoint(): string {
  return `https://cognito-idp.${getAuthConfig().region}.amazonaws.com/`;
}

async function callCognito<T>(action: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(cognitoEndpoint(), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    // __type is either "NotAuthorizedException" or "prefix#NotAuthorizedException"
    const code = String(data.__type ?? "UnknownError").split("#").pop() as string;
    throw new CognitoError(code, data.message ?? data.Message ?? "Sign-in failed.");
  }
  return data as T;
}

interface InitiateAuthResponse {
  AuthenticationResult?: {
    AccessToken: string;
    RefreshToken?: string;
    ExpiresIn: number;
  };
}

function toTokens(result: InitiateAuthResponse): AuthTokens {
  const auth = result.AuthenticationResult;
  if (!auth) {
    // A challenge (MFA, new password) — the pool doesn't configure any, so treat it as a failure.
    throw new CognitoError("UnsupportedChallenge", "This account needs a sign-in step the app doesn't support.");
  }
  return { accessToken: auth.AccessToken, refreshToken: auth.RefreshToken, expiresIn: auth.ExpiresIn };
}

export async function signInWithPassword(email: string, password: string): Promise<AuthTokens> {
  const result = await callCognito<InitiateAuthResponse>("InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: getAuthConfig().clientId,
    AuthParameters: { USERNAME: email.trim().toLowerCase(), PASSWORD: password },
  });
  return toTokens(result);
}

export async function refreshSession(refreshToken: string): Promise<AuthTokens> {
  const result = await callCognito<InitiateAuthResponse>("InitiateAuth", {
    AuthFlow: "REFRESH_TOKEN_AUTH",
    ClientId: getAuthConfig().clientId,
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  });
  return toTokens(result);
}

/** Revoke a refresh token so it can't mint new access tokens. Best effort: errors are swallowed. */
export async function revokeRefreshToken(refreshToken: string): Promise<void> {
  try {
    await callCognito("RevokeToken", { Token: refreshToken, ClientId: getAuthConfig().clientId });
  } catch {
    // Signing out locally still succeeds
  }
}

// ---------------------------------------------------------------------------
// Google sign-in (hosted UI, authorization code + PKCE)
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function createRandomString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** The S256 PKCE challenge: base64url(SHA-256(verifier)). */
export async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

export function getCallbackUrl(origin: string = window.location.origin): string {
  return `${origin}/auth/callback`;
}

export function buildGoogleAuthorizeUrl(codeChallenge: string, state: string, origin?: string): string {
  const { hostedUiDomain, clientId } = getAuthConfig();
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: getCallbackUrl(origin),
    identity_provider: "Google",
    scope: "openid email profile",
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  return `${hostedUiDomain}/oauth2/authorize?${params.toString()}`;
}

export async function exchangeCode(code: string, codeVerifier: string): Promise<AuthTokens> {
  const { hostedUiDomain, clientId } = getAuthConfig();
  const response = await fetch(`${hostedUiDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId,
      code,
      redirect_uri: getCallbackUrl(),
      code_verifier: codeVerifier,
    }).toString(),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.access_token) {
    throw new CognitoError(data.error ?? "TokenExchangeFailed", data.error_description ?? "Google sign-in failed.");
  }
  return { accessToken: data.access_token, refreshToken: data.refresh_token, expiresIn: data.expires_in };
}
